//Libs
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Column, Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { ColDef } from "ag-grid-community";
import { RangeValue } from "@react-types/shared";
import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  toCalendarDate,
  today,
  ZonedDateTime,
} from "@internationalized/date";
import { Fragment, useEffect, useState } from "react";

//Local
import LoadingClipboardAnimation from "../../components/LoadingClipBoardAnimation";
import { WORK_RECORD_AND_TASKS_URL } from "../../urls";
import * as strings from "../../strings";
import { DbRead } from "../../database";
import { StyledTooltip } from "../../components/StyledTooltip";
import { useStiltNav } from "../../navigation";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import { ExistingCraftRecord } from "../../models/craft-record";
import {
  CraftTypes,
  getReadableCraftType,
  isValidCraftType,
} from "../../models/craft-types";
import { useSiteKeyLocationsStore } from "../../store/site-key-locations";
import { useUserDisplayNamesStore } from "../../store/user-display-names-map";
import WorkRecordTableColumnsDialog from "../../components/WorkRecordTableColumnsDialog";
import { getWRListTableCustomizedColumns } from "../../assets/js/getWRListTableCustomizedColumns";
import WorkRecordListPage from "./WorkRecordListPage";
import { useSiteKeyDocStore } from "../../store/site-key-doc";
import { useAuthStore } from "../../store/firebase-auth";
import { formatWRDataForExcelExport } from "../../assets/js/formatWRDataForExcelExport";
import { logger } from "../../logging";
import DateRangePicker from "../../components/DateRangePicker";

interface Props {
  siteKey: string;
}

export default function WorkRecordListContainer({ siteKey }: Props) {
  type UrlParams = { craftTypeValue: string };
  const { craftTypeValue } = useParams<UrlParams>();
  if (typeof craftTypeValue !== "string") {
    throw new Error(`craftTypeValue was not a string: ${craftTypeValue}`);
  }

  const isValidCT = isValidCraftType(parseInt(craftTypeValue));

  const [userDisplayNamesMap, userDisplayNamesMapIsLoading] =
    useUserDisplayNamesStore((state) => [
      state.userDisplayNames,
      state.loading,
    ]);

  const [getLocationTitle, locationStoreIsLoading, siteKeyLocationList] =
    useSiteKeyLocationsStore((state) => [
      state.getLocationTitle,
      state.loading,
      state.siteKeyLocationList,
    ]);

  const stiltNav = useStiltNav();

  const [siteKeyDoc, siteKeyDocIsLoading] = useSiteKeyDocStore((state) => [
    state.siteKeyDoc,
    state.loading,
  ]);

  const firebaseUser = useAuthStore((state) => state.firebaseUser);

  /* HISTORY */
  const navigate = useNavigate();

  function goToWorkRecordAndTasksPage(
    craftRecordID: ExistingCraftRecord["id"],
  ) {
    navigate(`${WORK_RECORD_AND_TASKS_URL}/${craftRecordID}`);
  }

  const [range, setRange] = useState<RangeValue<CalendarDate>>({
    start: today(getLocalTimeZone()).subtract({
      days: siteKeyDoc?.customizations?.defaultInvoicePageDateRangeDays ?? 30,
    }),
    end: today(getLocalTimeZone()),
  });

  const [workRecordList, setWorkRecordList] = useState<ExistingCraftRecord[]>(
    [],
  );

  useEffect(() => {
    async function getParentRecords() {
      logger.debug("getParentRecords useEffect called ");
      // Query invoices via realtime updates. Set the list of invoices.
      const unsubscribeAllInDateRange =
        DbRead.parentRecords.subscribeAllInDateRange({
          siteKey: siteKey,
          open: craftTypeValue === "closed" ? false : true,
          dateRange: range,
          onChange: (craftRecordList: ExistingCraftRecord[]) => {
            setWorkRecordList(craftRecordList);
          },
          onError: (error) =>
            logger.error(`Error in getParentRecords: ${error.message}`),
        });
      return unsubscribeAllInDateRange;
    }

    // Store the returned 'unsubscribe' ƒn into the unsubscribePromise variable.
    const unsubscribePromise = getParentRecords();
    // Return an anonymous ƒn for cleanup.
    return () => {
      logger.debug(
        "(WorkRecordListContainers) getParentRecords useEffect return ƒn just invoked.",
      );
      unsubscribePromise.then((unsubscribe) => {
        if (unsubscribe) {
          logger.debug("Now unsubscribing from craftRecords.");
          unsubscribe();
        }
      });
      logger.debug("Done running cleanup ƒn for getParentRecords useEffect.");
    };
  }, [siteKey, range]);

  /**
   * Query to get the list of vehicles
   */
  const vehicleListQueryKey = ["workRecordListContainer_vehicle", siteKey];
  const { data: vehicleList = [], isLoading: vehicleListIsLoading } = useQuery(
    vehicleListQueryKey,
    () => DbRead.vehicles.getAll(siteKey),
  );

  const siteKeyCraftDetails: Record<string, any> =
    siteKeyDoc?.customizations.craftDetails;

  /* USE STATE */
  const {
    additionalColumnsSelection,
    additionalColumns,
    workRecordTableColumnsDefaultValues,
  } = getWRListTableCustomizedColumns(siteKeyCraftDetails);

  const localStorageColumnSelection = localStorage.getItem(
    "workRecordTableColumnsSelection",
  );

  const [
    workRecordTableColumnsDialogOpen,
    setWorkRecordTableColumnsDialogOpen,
  ] = useState<boolean>(false);
  const [workRecordTableColumnsSelection, setWorkRecordTableColumnsSelection] =
    useState<Record<string, boolean>>(
      localStorageColumnSelection
        ? JSON.parse(localStorageColumnSelection)
        : workRecordTableColumnsDefaultValues,
    );

  /**
   * Build an xlsx spreadsheet.
   */
  async function saveSpreadsheet(customColumns: ColDef[]) {
    const fileName = getFileName(isValidCT, craftTypeValue);

    const columns = [
      {
        key: "title",
        header: "Title",
      },
      {
        key: "numOpenTasks",
        header: "Open Tasks",
      },
      {
        key: "createdBy",
        header: "Created By",
      },
      {
        key: "timestampRecordCreated",
        header: "Time Created",
      },
    ] as Column[];

    if (vehicleList.length >= 1) {
      const assetColumn = {
        key: "assetID",
        header: "Asset",
      } as Column;
      columns.push(assetColumn);
    }

    if (siteKeyLocationList.length >= 1) {
      const locationColumn = {
        key: "locationID",
        header: "Location",
      } as Column;
      columns.push(locationColumn);
    }

    if (siteKeyDoc?.validCraftTypes.includes(CraftTypes.SCAFFOLDING)) {
      const daysStandingColumn = {
        key: "craftDetails.timestampMarkedInstalled",
        header: "# Days Standing",
      } as Column;
      columns.push(daysStandingColumn);
    }

    Object.values(customColumns).forEach((columnDetails) => {
      const additionalColumns = {
        key: columnDetails.field,
        header: columnDetails.headerName,
      } as Column;
      columns.push(additionalColumns);
    });

    const wb = new Workbook();
    const sheet = wb.addWorksheet(fileName);

    sheet.columns = columns;

    const formattedData = formatWRDataForExcelExport(
      workRecordList,
      userDisplayNamesMap,
      getLocationTitle,
      vehicleList,
    );

    sheet.addRows(formattedData);

    // Insert the user's report name as the title row (first row)
    const customTitle = `${
      userDisplayNamesMap[firebaseUser?.uid ?? ""]
    } [${fileName}]`;
    sheet.insertRow(1, [customTitle]);

    // updated the font for first row.
    sheet.getRow(1).font = { bold: true };
    sheet.getRow(2).font = { bold: true };

    try {
      // write the content using writeBuffer
      const buf = await wb.xlsx.writeBuffer();

      // download the processed file
      saveAs(new Blob([buf]), `${customTitle}.xlsx`);
    } catch (error) {
      console.error("Something Went Wrong", error);
    } finally {
      // removing worksheet's instance to create new one
      wb.removeWorksheet(fileName);
    }
  }

  // This function is called when the date range changes.
  // Converts the date range to CalendarDate objects and sets the range state.
  // The React-Aria DateRangePicker component requires its "onChange"
  // to use "RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>"
  // objects (not just "RangeValue<CalendarDate>" objects) and doesn't work
  // without this conversion.
  function handleDateRangeChange(
    value: RangeValue<CalendarDate | ZonedDateTime | CalendarDateTime>,
  ) {
    logger.debug(
      `Invoked handleDateRangeChange for range: ${value.start.month}-${value.start.day} to ${value.end.month}-${value.end.day}`,
    );
    try {
      const convertedStart = toCalendarDate(value.start);
      const convertedEnd = toCalendarDate(value.end);
      const converted = { start: convertedStart, end: convertedEnd };
      setRange(converted);
      logger.debug(
        `set range to ${converted.start.month}-${converted.start.day} to ${converted.end.month}-${converted.end.day}`,
      );
    } catch (e) {
      // TODO: Handle this error better
      if (e instanceof Error) {
        console.log("Error in handleDateRangeChange", e);
        logger.error("Error in handleDateRangeChange", e);
      }
    }
  }

  const dateRangePicker = (
    <DateRangePicker value={range} onChange={handleDateRangeChange} />
  );

  const createNewTaskButton = (
    <BaseButtonPrimary
      type="button"
      onClick={() => stiltNav.createTaskForCustomer({})}
      className="w-full text-primary sm:w-48"
    >
      <AddCircleIcon fontSize="small" className="mr-2" />
      {strings.NEW_TASK}
    </BaseButtonPrimary>
  );

  const actionButtons = (
    <div className="flex gap-4">
      <StyledTooltip title={`Export to Excel`}>
        <button
          onClick={() => saveSpreadsheet(additionalColumns)}
          className="rounded-md bg-gray-200 p-2"
        >
          <FileDownloadIcon fontSize="medium" className="shrink-0" />
        </button>
      </StyledTooltip>
      <StyledTooltip title={`Add Additional Columns`}>
        <button
          onClick={() => setWorkRecordTableColumnsDialogOpen(true)}
          className="rounded-md bg-gray-200 p-2"
        >
          <ViewColumnIcon fontSize="medium" className="shrink-0" />
        </button>
      </StyledTooltip>
    </div>
  );

  const workRecordTableColumnsDialog = (
    <WorkRecordTableColumnsDialog
      isOpen={workRecordTableColumnsDialogOpen}
      onClose={() => setWorkRecordTableColumnsDialogOpen(false)}
      setWorkRecordTableColumnsSelection={setWorkRecordTableColumnsSelection}
      getLocationTitle={getLocationTitle}
      goToWorkRecordAndTasksPage={goToWorkRecordAndTasksPage}
      additionalColumnsSelection={additionalColumnsSelection}
      workRecordTableColumnsSelection={workRecordTableColumnsSelection}
      vehicleList={vehicleList}
      siteKeyLocationList={siteKeyLocationList}
      validCraftTypes={siteKeyDoc?.validCraftTypes}
    />
  );

  // RENDER LOADING
  if (
    locationStoreIsLoading ||
    siteKeyDocIsLoading ||
    userDisplayNamesMapIsLoading ||
    vehicleListIsLoading
  ) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <LoadingClipboardAnimation />
      </div>
    );
  }

  //RENDER CONTENT
  return (
    <Fragment>
      <WorkRecordListPage
        workRecordList={workRecordList}
        workRecordTableColumnsSelection={workRecordTableColumnsSelection}
        goToWorkRecordAndTasksPage={goToWorkRecordAndTasksPage}
        getLocationTitle={getLocationTitle}
        userDisplayNamesMap={userDisplayNamesMap}
        vehicleList={vehicleList}
        customColumns={additionalColumns}
        siteKeyLocationList={siteKeyLocationList}
        validCraftTypes={siteKeyDoc?.validCraftTypes}
      >
        {{
          CreateNewTaskButton: createNewTaskButton,
          ActionButtons: actionButtons,
          DateRangePicker: dateRangePicker,
        }}
      </WorkRecordListPage>
      {workRecordTableColumnsDialog}
    </Fragment>
  );
}

function getFileName(isValidCT: boolean, craftTypeValue: string | undefined) {
  if (isValidCT) {
    return `${getReadableCraftType(craftTypeValue)} Records`;
  }
  if (craftTypeValue === "all") {
    return strings.ALL_RECORDS;
  }
  return strings.CLOSED_RECORDS;
}
